/**
 * Helpers that build the HTML body for "Create Proposal" notes that show up
 * on the contact timeline whenever a user downloads a PDF, opens a web view,
 * copies a PDF share link, or emails a proposal.
 *
 * The rendering side (`sanitizeProposalActivityHtml` in ContactDetail.tsx)
 * only preserves anchor hrefs for two cases:
 *   1. `/artworks/*` internal links — always clickable for everyone.
 *   2. `<a data-proposal-link="webView|copyPdfLink">` — clickable ONLY for
 *      the viewer who authored the note, so each seller sees their own
 *      share link but not their colleague's.
 *
 * Keep this helper the single source of truth for the anchor markup so that
 * the sanitizer and the producers stay in sync.
 */

export type ProposalNoteAction =
  | "download"
  | "webView"
  | "copyPdfLink"
  | "sendMail";

/** HTML-escape a raw string before embedding it inside `noteHtml`. */
export function escapeProposalNoteText(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export interface BuildProposalNoteBodyOptions {
  /**
   * Public URL generated by the action. Only used for `webView`/`copyPdfLink`.
   * When provided (and the action matches), the verb phrase becomes an
   * anchor tag tagged with `data-proposal-link` so the sanitizer can gate
   * its clickability by viewer identity. Pass `null`/empty to keep the
   * phrase as plain text (fallback for legacy call sites).
   */
  shareLink?: string | null;
  /**
   * Verb variant for `webView`. Defaults to `"created"` (matches the phrasing
   * used by ProposalModal + ArtworkDetail). `ArtworksNew` historically uses
   * `"opened"` — pass that value to preserve existing wording.
   */
  webViewVerb?: "created" | "opened";
  /**
   * Verb variant for `copyPdfLink`. Defaults to `"generated"` (ProposalModal +
   * ArtworkDetail). Pass `"copied"` for the ArtworksNew phrasing.
   */
  copyPdfVerb?: "generated" | "copied";
}

/**
 * Build the `<p>…</p>` note body that gets pushed to `mail.message.body` via
 * `postInternalNote`. Contains:
 *  - A bold author name (escaped).
 *  - A verb phrase describing the action. For `webView`/`copyPdfLink` the
 *    phrase ("web proposal view" / "proposal PDF link") is wrapped in an
 *    anchor tagged with `data-proposal-link` when a `shareLink` is passed.
 *  - A list of artwork anchor tags that must already be safely HTML-encoded
 *    by the caller (they point at `/artworks/:id`).
 *
 * The helper intentionally stays string-based so existing call sites that
 * pre-build `artworkLinksHtml` via `map(...).join(", ")` keep working
 * unchanged.
 */
export function buildProposalNoteBody(
  userName: string,
  action: ProposalNoteAction,
  artworkLinksHtml: string,
  opts: BuildProposalNoteBodyOptions = {},
): string {
  const safeUser = escapeProposalNoteText(userName);
  const rawShare = (opts.shareLink || "").trim();
  const safeShare = rawShare ? escapeProposalNoteText(rawShare) : "";

  const wrapShareAnchor = (
    phrase: string,
    kind: "webView" | "copyPdfLink",
  ): string => {
    if (!safeShare) return phrase;
    return `<a data-proposal-link="${kind}" href="${safeShare}" target="_blank" rel="noopener noreferrer">${phrase}</a>`;
  };

  const webViewVerb =
    opts.webViewVerb === "opened" ? "has opened" : "has created";
  const copyPdfVerb =
    opts.copyPdfVerb === "copied" ? "has copied" : "has generated";

  let verbPhrase: string;
  switch (action) {
    case "download":
      verbPhrase = "has downloaded";
      break;
    case "webView":
      verbPhrase = `${webViewVerb} a ${wrapShareAnchor(
        "web proposal view",
        "webView",
      )} for`;
      break;
    case "copyPdfLink":
      verbPhrase = `${copyPdfVerb} a ${wrapShareAnchor(
        "proposal PDF link",
        "copyPdfLink",
      )} for`;
      break;
    case "sendMail":
    default:
      verbPhrase = "has sent an email for";
      break;
  }

  return `<p><strong>${safeUser}</strong> ${verbPhrase} ${artworkLinksHtml}.</p>`;
}
